<template>
  <el-form
    :model="form"
    :rules="rules"
    :validateOnRuleChange="false"
    @submit.prevent.native
    ref="form"
    class="block p-t"
  >
    <el-collapse-transition>
      <div class="block p-b" v-if="serverError">
        <el-alert
          :title="$t('SHARED__SERVER_ERROR')"
          type="error"
          effect="dark"
          key="alert-success"
        >
        </el-alert>
      </div>
    </el-collapse-transition>
    <el-form-item
      prop="firstname"
      :error="violations.recipientFirstname"
      class="block with-control-label"
    >
      <el-input
        v-model="form.firstname"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        id="firstname"
      >
        <label
          for="firstname"
          slot="prepend"
          :class="[isLabelActive('firstname') ? 'active' : '']"
          >{{ $t("SHARED__FIRSTNAME_PLACEHOLDER") }}<sup>*</sup></label
        >
      </el-input>
    </el-form-item>

    <el-form-item
      prop="lastname"
      :error="violations.recipientLastname"
      class="block with-control-label"
    >
      <el-input
        v-model="form.lastname"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        id="lastname"
      >
        <label
          for="lastname"
          slot="prepend"
          :class="[isLabelActive('lastname') ? 'active' : '']"
          >{{ $t("SHARED__LASTNAME_PLACEHOLDER") }}<sup>*</sup></label
        >
      </el-input>
    </el-form-item>

    <el-form-item
      prop="zip"
      :error="violations.zip"
      class="block with-control-label"
      v-show="!zip"
    >
      <el-input
        v-model="form.zip"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        id="zip"
      >
        <label
          for="zip"
          slot="prepend"
          :class="[isLabelActive('zip') ? 'active' : '']"
          >{{ $t("SHARED__PC_PLACEHOLDER") }}<sup>*</sup></label
        >
      </el-input>
    </el-form-item>

    <el-form-item prop="isSubscribedToContestMessages" v-if="!existingUser">
      <el-checkbox
        v-model="form.isSubscribedToContestMessages"
        name="isSubscribedToContestMessages"
        >{{ $t("SHARED__AGREEMENT") }} <br />{{ $t("SHARED__OPTIN_TRANSAT")
        }}<sup>*</sup></el-checkbox
      >
    </el-form-item>

    <el-form-item v-show="false" style="margin-top: 45px">
      <el-checkbox
        v-model="form.isSubscribedToAirTransatMessages"
        name="isSubscribedToAirTransatMessages"
        >{{ $t("SHARED__OPTIN_TRANSAT") }} ({{
          $t("SHARED__OPTIONAL")
        }})</el-checkbox
      >
    </el-form-item>

    <el-form-item>
      <el-button type="primary" @click="submit" class="fnt w100">
        {{ $t("ACCEPT__BTN_IN") }}
        <i :class="[loading ? 'fas fa-spinner fa-spin' : 'fas fa-plane']"></i>
      </el-button>
    </el-form-item>
  </el-form>
</template>

<script>
import to from "await-to-js"
import { createNamespacedHelpers } from "vuex"

import InvitationService from "../../services/api/InvitationService"

const { mapState, mapMutations } = createNamespacedHelpers("app")
const invitationService = new InvitationService()

export default {
  name: "AcceptInvitationForm",
  props: {
    existingUser: { type: Boolean, required: true },
    firstname: { type: String },
    lastname: { type: String },
    zip: { type: String }
  },
  data() {
    return {
      loading: false,
      inFocus: null,
      form: {
        firstname: null,
        lastname: null,
        zip: null,
        isSubscribedToContestMessages: false,
        isSubscribedToAirTransatMessages: true,
        isSubscribedToIdcMessages: false
      },
      isFormSubmitted: false,
      violations: {},
      serverError: false,
      invitationId: null,
      token: null
    }
  },
  computed: {
    ...mapState(["selectedDestination"]),
    rules() {
      var validateIsSubscribedToContestMessages = (rule, value, callback) => {
        if (!value) {
          callback(new Error(this.$t("SHARED__NOT_EMPTY_VALIDATION")))
        } else {
          callback()
        }
      }
      return {
        zip: [
          {
            required: true,
            pattern: /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z]\s?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i,
            message: this.$t("SHARED__POSTAL_CODE_VALIDATION"),
            trigger: "blur"
          }
        ],
        firstname: [
          {
            required: true,
            min: 2,
            message: this.$t("SHARED__MIN_LENGTH_VALIDATION").replace("%d", 2),
            trigger: "blur"
          }
        ],
        lastname: [
          {
            required: true,
            min: 2,
            message: this.$t("SHARED__MIN_LENGTH_VALIDATION").replace("%d", 2),
            trigger: "blur"
          }
        ],
        isSubscribedToContestMessages: [
          {
            type: "boolean",
            validator: validateIsSubscribedToContestMessages
          }
        ]
      }
    }
  },
  watch: {
    "form.isSubscribedToContestMessages"(val) {
      this.form.isSubscribedToAirTransatMessages = val
    }
  },
  methods: {
    ...mapMutations(["setUser"]),
    submit() {
      if (this.isFormSubmitted) return
      this.violations = {}
      this.serverError = false
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.isFormSubmitted = true
          this.loading = true

          let err, invitation
          ;[err, invitation] = await to(
            invitationService.acceptInvitation({
              invitationId: this.invitationId,
              recipientFirstname: this.form.firstname,
              recipientLastname: this.form.lastname,
              recipientEmail: this.form.email,
              isSubscribedToAirTransatMessages: this.form
                .isSubscribedToAirTransatMessages,
              isSubscribedToIdcMessages: this.form.isSubscribedToIdcMessages,
              zip: this.form.zip,
              token: this.token
            })
          )

          if (err) {
            this.isFormSubmitted = false
            this.loading = false
            if (err.response.status !== 500) {
              const violations = err.response.data.violations
              violations.forEach(violation => {
                this.violations[violation.propertyPath] = violation.message
              })
            } else {
              this.serverError = true
            }

            return
          }

          this.isFormSubmitted = false
          this.loading = false

          this.$emit("onAccept", invitation)
        } else {
          return false
        }
      })
    },
    isLabelActive(field) {
      return this.inFocus === field || this.form[field]
    },
    handleInputFocus(evt) {
      this.inFocus = evt.target.id
    },
    handleInputBlur(evt) {
      const id = evt.target.id
      if (this.inFocus === id) {
        this.inFocus = null
      }
    }
  },
  created() {
    if (this.firstname) {
      this.form.firstname = this.firstname
    }
    if (this.lastname) {
      this.form.lastname = this.lastname
    }
    if (this.email) {
      this.form.email = this.email
    }
    if (this.zip) {
      this.form.zip = this.zip
    }

    this.invitationId = this.$route.query.i
    this.token = this.$route.query.t
  }
}
</script>

<style scoped></style>
